import { CheckExportBtn } from '@/components/CheckExportBtn';
import { handleExport, REPORT_EXPORT_COLS } from '@/lib/exportCsv';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, AlertTriangle, AlertCircle, CheckCircle2, Clock, Trash2, RefreshCcw, FileText, Users, Sparkles, Paperclip, Download, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { toLocalYmd } from '@/lib/dateRange';

const STATUS_META = {
  overdue:  { icon: AlertTriangle, label: 'Overdue',  class: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border-rose-300', tile: 'border-rose-300 bg-rose-50 dark:bg-rose-950/20' },
  due_soon: { icon: AlertCircle,   label: 'Due Soon', class: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-300', tile: 'border-amber-300 bg-amber-50 dark:bg-amber-950/20' },
  upcoming: { icon: Clock,         label: 'Upcoming', class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-300', tile: 'border-blue-300 bg-blue-50 dark:bg-blue-950/20' },
  ok:       { icon: CheckCircle2,  label: 'OK',       class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-300', tile: 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20' },
};

const EMPTY = {
  employee_id: '', document_type: 'iqama',
  document_number: '', issued_date: '',
  expiry_date: '', advance_days: 60,
  issuing_country: '', issuing_authority: '',
  notes: '',
};

export default function HRDocumentsPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [dashboard, setDashboard] = useState({ counts: {}, employee_risk: [], total_active: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const [renewOpen, setRenewOpen] = useState(false);
  const [renewing, setRenewing] = useState(null);
  const [renewForm, setRenewForm] = useState({ new_expiry_date: '', document_number: '', note: '' });

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRenewOpen, setBulkRenewOpen] = useState(false);
  const [bulkRenewForm, setBulkRenewForm] = useState({ new_expiry_date: '', note: '' });

  const fetchAll = async () => {
    try {
      const params = employeeFilter ? { employee_id: employeeFilter } : {};
      const [list, dash, t, emp] = await Promise.all([
        api.get('/hr/documents', { params }),
        api.get('/hr/documents/dashboard'),
        api.get('/hr/documents/types'),
        api.get('/employees'),
      ]);
      setItems(list.data.items || []);
      setDashboard(dash.data || { counts: {}, total_active: 0 });
      setTypes(t.data || []);
      setEmployees(emp.data || []);
    } catch { toast.error('Failed to load HR documents'); }
  };

  useEffect(() => { fetchAll(); }, [employeeFilter]);

  const save = async () => {
    if (!form.employee_id || !form.expiry_date) { toast.error('Employee + expiry date required'); return; }
    try {
      if (editing) await api.put(`/hr/documents/${editing.id}`, form);
      else         await api.post('/hr/documents', form);
      toast.success(editing ? 'Updated' : 'Created');
      setFormOpen(false);
      setEditing(null);
      setForm(EMPTY);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const startEdit = (r) => {
    setEditing(r);
    setForm({
      employee_id: r.employee_id, document_type: r.document_type,
      document_number: r.document_number || '', issued_date: r.issued_date || '',
      expiry_date: r.expiry_date || '', advance_days: r.advance_days || 60,
      issuing_country: r.issuing_country || '', issuing_authority: r.issuing_authority || '',
      notes: r.notes || '',
    });
    setFormOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document record?')) return;
    try { await api.delete(`/hr/documents/${id}`); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Delete failed'); }
  };

  const startRenew = (r) => {
    setRenewing(r);
    const cur = new Date(r.expiry_date);
    cur.setFullYear(cur.getFullYear() + 1);
    setRenewForm({ new_expiry_date: toLocalYmd(cur), document_number: r.document_number || '', note: '' });
    setRenewOpen(true);
  };

  const doRenew = async () => {
    try {
      await api.post(`/hr/documents/${renewing.id}/renew`, renewForm);
      toast.success('Renewed');
      setRenewOpen(false); setRenewing(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const selectAllVisible = (visibleItems) => {
    if (selectedIds.size === visibleItems.length && visibleItems.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleItems.map((i) => i.id)));
    }
  };
  const doBulkRenew = async () => {
    if (!bulkRenewForm.new_expiry_date) { toast.error('Pick a new expiry date'); return; }
    if (selectedIds.size === 0) { toast.error('Select at least one document'); return; }
    try {
      const r = await api.post('/hr/documents/bulk-renew', {
        ids: Array.from(selectedIds),
        new_expiry_date: bulkRenewForm.new_expiry_date,
        note: bulkRenewForm.note,
      });
      toast.success(`Renewed ${r.data.renewed} document(s)`);
      setBulkRenewOpen(false);
      setSelectedIds(new Set());
      setBulkRenewForm({ new_expiry_date: '', note: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const seedSample = async () => {
    try {
      const r = await api.post('/hr/documents/seed-sample');
      toast.success(`Seeded ${r.data.created} sample document(s) across ${r.data.total_employees_used} employee(s)`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const uploadFile = async (docId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/hr/documents/${docId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File attached');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const downloadFile = async (docId) => {
    try {
      const res = await api.get(`/hr/documents/${docId}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Download failed'); }
  };

  const removeFile = async (docId) => {
    if (!window.confirm('Remove the attached file?')) return;
    try {
      await api.delete(`/hr/documents/${docId}/file`);
      toast.success('Attachment removed');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const filteredItems = statusFilter === 'all' ? items : items.filter(i => i.status === statusFilter);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="hr-documents-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-700 flex items-center justify-center shadow-lg">
              <FileText size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Employee Documents</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Visa, Iqama, Passport, Work Permit, Medical &mdash; expiry tracking with auto-alerts
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={employeeFilter || 'all'} onValueChange={(v) => setEmployeeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="hr-emp-filter">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name || `${e.first_name||''} ${e.last_name||''}`.trim()}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedIds.size > 0 && (
              <Button onClick={() => setBulkRenewOpen(true)} variant="outline" data-testid="bulk-renew-btn"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20">
                <RefreshCcw size={14} className="mr-1" /> Bulk Renew ({selectedIds.size})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={seedSample} data-testid="seed-sample-btn">
              <Sparkles size={12} className="mr-1" /> Seed sample
            </Button>
            <Button onClick={() => { setEditing(null); setForm(EMPTY); setFormOpen(true); }}
              data-testid="add-hr-doc-btn" className="bg-indigo-500 hover:bg-indigo-600 text-white">
              <Plus size={14} className="mr-1" /> Add Document
            </Button>
          </div>
        </div>

        {/* Status tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['overdue', 'due_soon', 'upcoming', 'ok'].map(k => {
            const meta = STATUS_META[k];
            const Icon = meta.icon;
            return (
              <Card key={k} className={`cursor-pointer border-2 transition-all ${meta.tile} ${statusFilter === k ? 'ring-2 ring-foreground' : ''}`}
                onClick={() => setStatusFilter(statusFilter === k ? 'all' : k)}
                data-testid={`hr-tile-${k}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between">
                    <Icon size={22} />
                    <span className="text-3xl font-bold font-outfit">{dashboard.counts?.[k] || 0}</span>
                  </div>
                  <p className="text-xs uppercase font-bold mt-1 tracking-wider">{meta.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* At-risk roster */}
        {dashboard.employee_risk?.length > 0 && (
          <Card className="border-amber-300 bg-amber-50/40 dark:bg-amber-900/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <Users size={14}/> Employees with expiring documents
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                    <tr><th className="text-left px-4 py-1.5">Employee</th><th className="text-left px-4 py-1.5">Documents</th><th className="text-left px-4 py-1.5">Worst Status</th></tr>
                  </thead>
                  <tbody>
                    {dashboard.employee_risk.slice(0, 10).map(r => {
                      const meta = STATUS_META[r.worst_status] || STATUS_META.ok;
                      return (
                        <tr key={r.employee_id} className="border-b border-border">
                          <td className="px-4 py-1.5 font-medium">{r.employee_name}</td>
                          <td className="px-4 py-1.5 text-xs">
                            {r.documents.map((d, i) => (
                              <span key={i} className="inline-block mr-2 mb-0.5 px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">
                                {d.type}: {d.days_remaining < 0 ? `${-d.days_remaining}d ago` : `${d.days_remaining}d`}
                              </span>
                            ))}
                          </td>
                          <td className="px-4 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${meta.class}`}>{meta.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><FileText size={14}/> All Documents {statusFilter !== 'all' ? `(${STATUS_META[statusFilter].label})` : ''}
              <CheckExportBtn
                disabled={!filteredItems.length}
                testId="check-export-iqama"
                title="Validate document rows (Iqama, Visa, Passport...), then export CSV"
                onClick={() => handleExport(
                  filteredItems.map((r) => ({ counterparty: r.employee_name || '', date: r.expiry_date, amount: 0, status: `${r.document_type || ''} · ${r.status || ''}` })),
                  'iqama_documents_report.csv', REPORT_EXPORT_COLS, 'documents',
                )}
              />
            </CardTitle>
            {statusFilter !== 'all' && <Button size="sm" variant="ghost" onClick={() => setStatusFilter('all')}>Clear filter</Button>}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox"
                        checked={filteredItems.length > 0 && selectedIds.size === filteredItems.length}
                        onChange={() => selectAllVisible(filteredItems)}
                        data-testid="select-all-docs"
                        className="rounded" />
                    </th>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Document</th>
                    <th className="text-left px-4 py-2">Number</th>
                    <th className="text-left px-4 py-2">Expiry</th>
                    <th className="text-left px-4 py-2">Days</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No documents tracked yet. Click "Add Document" to register your first one, or "Seed sample" to populate test data.</td></tr>
                  ) : filteredItems.map(r => {
                    const meta = STATUS_META[r.status] || STATUS_META.ok;
                    const Icon = meta.icon;
                    const typeLabel = types.find(t => t.key === r.document_type)?.label || r.document_type;
                    return (
                      <tr key={r.id} className="border-b border-border hover:bg-muted/30" data-testid={`hr-doc-${r.id}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox"
                            checked={selectedIds.has(r.id)}
                            onChange={() => toggleSelect(r.id)}
                            data-testid={`select-doc-${r.id}`}
                            className="rounded" />
                        </td>
                        <td className="px-4 py-2 font-medium">{r.employee_name || '—'}</td>
                        <td className="px-4 py-2 text-xs">{typeLabel}</td>
                        <td className="px-4 py-2 text-xs font-mono">{r.document_number || '—'}</td>
                        <td className="px-4 py-2 text-xs font-mono">{r.expiry_date}</td>
                        <td className="px-4 py-2 text-xs font-mono font-semibold">
                          {r.days_remaining !== null
                            ? r.days_remaining < 0
                              ? <span className="text-rose-600">{Math.abs(r.days_remaining)}d ago</span>
                              : <span className={r.days_remaining <= 7 ? 'text-amber-600' : 'text-foreground'}>{r.days_remaining}d</span>
                            : '—'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${meta.class}`}>
                            <Icon size={11} /> {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            {r.has_file ? (
                              <>
                                <button onClick={() => downloadFile(r.id)} className="px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-medium dark:bg-blue-900/30 dark:text-blue-400" title="Download attached file" data-testid={`download-file-${r.id}`}>
                                  <Download size={11} className="inline" />
                                </button>
                                <button onClick={() => removeFile(r.id)} className="px-2 py-1 rounded hover:bg-error/10 text-error text-xs" title="Remove attachment">
                                  <X size={11} />
                                </button>
                              </>
                            ) : (
                              <label className="px-2 py-1 rounded bg-stone-100 text-muted-foreground hover:bg-muted text-xs font-medium cursor-pointer dark:bg-stone-800/40" title="Attach scan (PDF/PNG/JPG, max 10MB)">
                                <Paperclip size={11} className="inline" />
                                <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                                  onChange={(e) => { uploadFile(r.id, e.target.files[0]); e.target.value = ''; }}
                                  data-testid={`upload-file-${r.id}`} />
                              </label>
                            )}
                            <button onClick={() => startRenew(r)} className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-xs font-medium dark:bg-emerald-900/30 dark:text-emerald-400" data-testid={`renew-doc-${r.id}`}>
                              <RefreshCcw size={11} className="inline" /> Renew
                            </button>
                            <button onClick={() => startEdit(r)} className="px-2 py-1 rounded hover:bg-muted text-xs">Edit</button>
                            <button onClick={() => remove(r.id)} className="px-2 py-1 rounded hover:bg-error/10 text-error text-xs"><Trash2 size={11} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit dialog */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? 'Edit Document' : 'Add Employee Document'}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Employee *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="hr-doc-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name || `${e.first_name||''} ${e.last_name||''}`.trim()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Document Number</Label>
                  <Input value={form.document_number} onChange={(e) => setForm({ ...form, document_number: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Issued</Label>
                  <Input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Expiry *</Label>
                  <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="hr-doc-expiry" />
                </div>
                <div>
                  <Label className="text-xs">Alert Before</Label>
                  <Select value={String(form.advance_days)} onValueChange={(v) => setForm({ ...form, advance_days: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{[7, 14, 30, 60, 90].map(d => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Issuing Country</Label>
                  <Input value={form.issuing_country} onChange={(e) => setForm({ ...form, issuing_country: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Issuing Authority</Label>
                  <Input value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="save-hr-doc-btn">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Renew dialog */}
        <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Renew document for {renewing?.employee_name}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">New Expiry Date *</Label>
                <Input type="date" value={renewForm.new_expiry_date}
                  onChange={(e) => setRenewForm({ ...renewForm, new_expiry_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">New Document Number</Label>
                <Input value={renewForm.document_number}
                  onChange={(e) => setRenewForm({ ...renewForm, document_number: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Input value={renewForm.note} onChange={(e) => setRenewForm({ ...renewForm, note: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenewOpen(false)}>Cancel</Button>
              <Button onClick={doRenew} className="bg-emerald-500 hover:bg-emerald-600 text-white">Renew</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Renew dialog */}
        <Dialog open={bulkRenewOpen} onOpenChange={setBulkRenewOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Bulk Renew {selectedIds.size} document(s)</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-xs text-muted-foreground">All selected documents will be set to the same new expiry date and a bulk renewal entry will be added to each renewal_history.</p>
              <div>
                <Label className="text-xs">New Expiry Date *</Label>
                <Input type="date" value={bulkRenewForm.new_expiry_date}
                  onChange={(e) => setBulkRenewForm({ ...bulkRenewForm, new_expiry_date: e.target.value })}
                  data-testid="bulk-renew-date" />
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Input value={bulkRenewForm.note}
                  onChange={(e) => setBulkRenewForm({ ...bulkRenewForm, note: e.target.value })}
                  placeholder="e.g. Annual renewal batch" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkRenewOpen(false)}>Cancel</Button>
              <Button onClick={doBulkRenew} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="bulk-renew-confirm">
                Renew {selectedIds.size}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
